import React, { useState, useEffect } from "react";
import { WHATSAPP_URL } from "@/lib/brand";
import {
  Sparkles,
  Menu,
  X,
  ArrowUpRight,
} from "lucide-react";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#solution" },
  { label: "Pricing", href: "#pricing" },
  { label: "Process", href: "#how" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);

    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-2xl border-b border-[var(--gf-border)] shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 lg:h-20 flex items-center justify-between">
        
        {/* Logo */}
        <a
          href="#top"
          className="flex items-center gap-3 group"
          data-testid="brand-logo"
        >
          <div className="relative flex items-center justify-center h-10 w-10 rounded-2xl bg-[var(--gf-primary)] text-white shadow-lg">
            <Sparkles className="h-4 w-4" />

            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[var(--gf-green)] gf-pulse-dot" />
          </div>

          <div>
            <p className="font-display text-xl font-extrabold tracking-tight leading-none">
              Grow<span className="text-[var(--gf-blue)]">Flow</span>
            </p>

            <p className="text-[10px] text-[var(--gf-text-2)] uppercase tracking-widest mt-0.5">
              Digital Growth Agency
            </p>
          </div>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-[var(--gf-secondary)] hover:text-[var(--gf-primary)] transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        {/* Right Buttons */}
        <div className="flex items-center gap-3">
          
          {/* WhatsApp */}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="header-whatsapp-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-[var(--gf-green)] hover:bg-[var(--gf-green-deep)] text-white text-sm font-semibold px-5 h-11 rounded-full transition-all hover:scale-[1.03] shadow-lg"
          >
            <span className="h-2 w-2 rounded-full bg-white gf-pulse-dot" />

            WhatsApp
          </a>

          {/* Main CTA */}
          <a
            href="#lead-form"
            className="hidden md:inline-flex items-center gap-2 bg-[var(--gf-primary)] hover:bg-slate-800 text-white text-sm font-semibold px-5 h-11 rounded-full transition-all hover:scale-[1.03]"
          >
            Get Free Demo
            <ArrowUpRight className="h-4 w-4" />
          </a>

          {/* Mobile Menu */}
          <button
            className="md:hidden h-11 w-11 inline-flex items-center justify-center rounded-full border border-[var(--gf-border)] bg-white"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div
          className="md:hidden border-t border-[var(--gf-border)] bg-white/95 backdrop-blur-xl"
          data-testid="mobile-menu"
        >
          <div className="px-5 py-5 flex flex-col gap-4">
            
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-[var(--gf-primary)]"
              >
                {n.label}
              </a>
            ))}

            <a
              href="#lead-form"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex justify-center items-center gap-2 bg-[var(--gf-primary)] text-white font-semibold px-5 h-12 rounded-full"
            >
              Get Free Demo
            </a>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-[var(--gf-green)] text-white font-semibold px-5 h-12 rounded-full"
            >
              WhatsApp Now
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
